import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import { Modal } from "@/config/antd";
import { CloseOutlined } from "@/config/antd";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import TextEditor from "@/components/common/inputs/Text_Editor";
import FileInput from "@/components/common/inputs/File_Input";
import { useLoader } from "@/hooks/LoaderProvider";
import Success from "@/components/common/alerts/Success";
import Delete from "@/components/common/alerts/Delete";
import {
  createSupportTicketAsync,
  selectSupportError,
  selectSupportSuccess,
  clearSupportState,
} from "@/store/redux/slices/support/support_slice";
import { getOrganizationId } from "@/utils/token_functions";

const AddTicketModal = ({ isVisible, onClose }) => {
  const dispatch = useDispatch();
  const error = useSelector(selectSupportError);
  const success = useSelector(selectSupportSuccess);
  const { showLoader, hideLoader } = useLoader();

  const [formData, setFormData] = useState({
    title: "",
    type: "",
    description: "",
    attachment: null,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);

  const supportTypes = [
    { value: "High", label: "High Priority" },
    { value: "Medium", label: "Medium Priority" },
    { value: "Low", label: "Low Priority" },
  ];

  useEffect(() => {
    if (success) {
      setIsSuccessVisible(true);
      onClose();
      // Don't clear the state yet, as we want to keep success state to show the modal
    }
  }, [success, onClose]);

  const handleSubmit = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
  };

  const handleProceed = async () => {
    setShowDeleteModal(false);
    onClose();
    showLoader();

    try {
      // Sanitize the description to remove <p> tags and other HTML
      const sanitizedDescription = DOMPurify.sanitize(formData.description, {
        ALLOWED_TAGS: [], // Remove all HTML tags
        ALLOWED_ATTR: [], // Remove all attributes
      }).trim();

      // Get organization ID
      const organizationID = getOrganizationId();

      // Create the ticket with organization ID
      await dispatch(
        createSupportTicketAsync({
          title: formData.title,
          description: sanitizedDescription,
          type: formData.type,
          organizationID,
        })
      ).unwrap();

      setFormData({
        title: "",
        type: "",
        description: "",
        attachment: null,
      });
    } catch (err) {
      console.error("Failed to create ticket:", err);
    } finally {
      hideLoader();
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessVisible(false);
    dispatch(clearSupportState());
    // No need to refetch tickets - the reducer handles adding the new ticket to state
  };

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={500}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          <div className="p-4 flex justify-between items-center border-b border-gray-200">
            <h2 className="text-bold-x text-primary">
              Create New Support Ticket
            </h2>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-secondary)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          <div className="flex flex-col gap-4 p-6">
            <h1 className="text-regular font-bold">
              Enter ticket information below
            </h1>

            <Input
              label="TITLE"
              placeholder="Enter ticket title"
              value={formData.title}
              onChange={handleInputChange("title")}
            />

            <Input
              type="select"
              label="TYPE"
              placeholder="Select ticket type"
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              options={supportTypes}
            />

            <TextEditor
              label="DESCRIPTION"
              placeholder="Enter ticket description"
              value={formData.description}
              onChange={(value) =>
                setFormData({ ...formData, description: value })
              }
            />

            <FileInput
              label="ATTACHMENT"
              placeholder="Upload attachment"
              id="support-attachment"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              // onChange={handleFileChange}
            />
          </div>

          <div className="w-full bg-secondary p-4 flex gap-3">
            <Button
              text="Cancel"
              color="secondary"
              className="flex-1 border border-primary"
              customBgColor="white"
              customTextColor="primary"
              onClick={onClose}
              fontWeight="normal"
            />
            <Button
              text="Send"
              color="primary"
              className="flex-1"
              customBgColor="primary"
              customTextColor="white"
              onClick={handleSubmit}
              fontWeight="normal"
            />
          </div>
        </div>
      </Modal>

      <Delete
        isVisible={showDeleteModal}
        onClose={handleDeleteClose}
        onProceed={handleProceed}
        header="Create Ticket?"
        paragraph="Send message and create ticket."
        proceedButtonText="Send"
        cancelButtonText="Cancel"
      />

      <Success
        isVisible={isSuccessVisible}
        onClose={handleSuccessClose}
        header="Ticket Created"
        paragraph="New Ticket has been created and Veegil support team has been notified."
        buttonText="Close"
      />

      {error && (
        <Delete
          isVisible={true}
          onClose={() => dispatch(clearSupportState())}
          header="Error"
          paragraph={error}
          proceedButtonText="OK"
        />
      )}
    </>
  );
};

AddTicketModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
};

export default AddTicketModal;
